import { Skeleton, Button } from '@distribusion/owl-frontend-ui'
import { useFormikContext } from 'formik'
import React, { useCallback, useMemo, useState } from 'react'

import { useTranslation } from '@lib/i18n'
import { useDiscountCardsLoader } from '@loaders/discountCards'
import DiscountSelect from '@pages/Checkout/DiscountSelect'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useParams } from '@stores/params'

const DISCOUNT_TYPES_SCOPE: DiscountCard.Type[] = ['promocode', 'voucher']
const SUPPORTED_CONTEXTS: DiscountCard.Context[] = ['booking']

const VoucherDropdown: React.FC = () => {
  const { t } = useTranslation()
  const [{ locale, retailerPartnerNumber, marketingCarrierCode }, setParams] = useParams()
  const [error, setError] = useState<string>()
  const {
    values: { isVacancyLoading, vacancy, meta },
    setFieldValue,
  } = useFormikContext<CheckoutFormData>()

  const { isLoading, data: allDiscountCards } = useDiscountCardsLoader({
    locale,
    retailerPartnerNumber,
    types: DISCOUNT_TYPES_SCOPE,
    marketingCarrierCodes: marketingCarrierCode ? [marketingCarrierCode] : /* istanbul ignore next */ undefined,
  })

  const discountCards = useMemo(
    () => allDiscountCards?.filter(c => SUPPORTED_CONTEXTS.includes(c.context)),
    [allDiscountCards],
  )
  const activeCard = useMemo<DiscountCode.Card | undefined>(() => {
    if (!vacancy?.appliedCards.length) return undefined

    const activeCard = discountCards?.find(card => vacancy.appliedCards.some(applied => applied.name === card.name))

    return (
      activeCard && {
        name: activeCard.name,
        type: activeCard.cardType,
        code: vacancy.appliedCards.find(ap => ap.name === activeCard.name)?.code,
      }
    )
  }, [discountCards, vacancy?.appliedCards])

  const handleChange = useCallback(
    (card: DiscountCode.Card | null) => {
      const otherCards = meta.cards.filter(
        cardToBeApplied => !discountCards?.some(card => card.name === cardToBeApplied.name),
      )
      const cards = otherCards.concat(card ? [card] : [])

      setFieldValue('vacancies', [])
      setFieldValue('meta.cards', cards)
      setParams(params => ({
        ...params,
        cards: cards,
      }))
    },
    [setFieldValue, setParams, meta.cards, discountCards],
  )
  const handleRemove = useCallback(() => handleChange(null), [handleChange])

  if (isLoading)
    return (
      <div className="ui-dropdown__label">
        <Skeleton.Item width="100%" height={32} />
      </div>
    )
  if (!discountCards?.length) return null

  return (
    <div className="voucher-dropdown column gap-3">
      <DiscountSelect
        label={t('checkout.discounts.title')}
        value={activeCard}
        availableCards={discountCards}
        onChange={card => handleChange(card)}
        setError={error => setError(error)}
        disabled={isVacancyLoading}
        error={error}
        fullWidth
      />
      {activeCard && (
        <Button color="primary" variant="text" fullWidth={false} onClick={handleRemove} loading={isVacancyLoading}>
          <span>{t('checkout.discounts.removeCode')}</span>
        </Button>
      )}
    </div>
  )
}
export default VoucherDropdown
